import { create } from "zustand";
import { supabase } from "../lib/supabase";
import { ReactionSummary, RawReaction, aggregateReactions, useReactionStore } from "./reactionStore";
import { Comment, useCommentStore, extractProfile } from "./commentStore";

const ITEMS_PER_PAGE = 25;
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

export type ActivityType =
  | "list_created"
  | "place_added"
  | "photo_uploaded"
  | "list_saved";

export interface ActivityItem {
  id: string;
  user_id: string;
  activity_type: ActivityType;
  list_id: string | null;
  place_id: string | null;
  photo_id: string | null;
  saved_list_id: string | null;
  created_at: string;
  // Joined data
  profile: {
    id: string;
    username: string | null;
    full_name: string | null;
    avatar_url: string | null;
  } | null;
  list: {
    id: string;
    title: string;
  } | null;
  place: {
    id: string;
    name: string;
    rating: number | null;
    notes: string | null;
  } | null;
  photo: {
    id: string;
    file_url: string;
  } | null;
  // Reactions
  reactions: ReactionSummary[];
  user_reaction: string | null;
  // Comments
  comment_count: number;
}

export interface GroupedActivityItem extends ActivityItem {
  grouped_photos?: Array<{ id: string; file_url: string }>;
  photo_count?: number;
}

interface ActivityState {
  activities: ActivityItem[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  currentPage: number;
  totalCount: number;

  fetchFeed: (page?: number) => Promise<void>;
  loadMore: () => Promise<void>;
  refreshFeed: () => Promise<void>;
  clearActivities: () => void;
  clearError: () => void;
  updateActivityReaction: (
    activityId: string,
    action: "added" | "updated" | "removed",
    newReaction: string | null,
    previousReaction: string | null
  ) => void;
}

export const useActivityStore = create<ActivityState>((set, get) => ({
  activities: [],
  loading: false,
  error: null,
  hasMore: true,
  currentPage: 0,
  totalCount: 0,

  fetchFeed: async (page = 0) => {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      set({ error: "Must be logged in to view feed", loading: false });
      return;
    }

    set({ loading: true, error: null });

    try {
      const thirtyDaysAgo = new Date(Date.now() - THIRTY_DAYS_MS).toISOString();
      const from = page * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;

      const { data, error, count } = await supabase
        .from("activity_feed")
        .select(
          `
          id,
          user_id,
          activity_type,
          list_id,
          place_id,
          photo_id,
          saved_list_id,
          created_at,
          profile:profiles!user_id (
            id,
            username,
            full_name,
            avatar_url
          ),
          list:lists!list_id (
            id,
            title
          ),
          place:places!place_id (
            id,
            name,
            rating,
            notes
          ),
          photo:photos!photo_id (
            id,
            file_url
          ),
          raw_reactions:activity_reactions (
            reaction_type,
            user_id
          ),
          comments (
            id,
            activity_id,
            user_id,
            content,
            created_at,
            profile:profiles!user_id (
              id,
              username,
              full_name,
              avatar_url
            )
          )
        `,
          { count: "exact" },
        )
        .gte("created_at", thirtyDaysAgo)
        .order("created_at", { ascending: false })
        .range(from, to);

      if (error) throw error;

      // Get reaction types for aggregation
      const reactionTypes = useReactionStore.getState().reactionTypes;

      // Transform the data to match our ActivityItem interface
      const activities: ActivityItem[] = (data || []).map((item: Record<string, unknown>) => {
        const rawReactions: RawReaction[] = (item.raw_reactions as RawReaction[]) || [];
        const { reactions, user_reaction } = aggregateReactions(rawReactions, user.id, reactionTypes);

        // Extract comment data
        const rawComments = (item.comments as Record<string, unknown>[]) || [];
        const commentCount = rawComments.length;
        // Take the last 2 comments (most recent) for preview
        const previewComments: Comment[] = rawComments
          .sort((a, b) =>
            new Date(a.created_at as string).getTime() - new Date(b.created_at as string).getTime()
          )
          .slice(-2)
          .map((c) => ({
            id: c.id as string,
            activity_id: c.activity_id as string,
            user_id: c.user_id as string,
            content: c.content as string,
            created_at: c.created_at as string,
            profile: extractProfile(c.profile),
          }));

        const activityId = item.id as string;
        useCommentStore.getState().setPreviewComments(activityId, previewComments, commentCount);

        return {
          id: activityId,
          user_id: item.user_id as string,
          activity_type: item.activity_type as ActivityType,
          list_id: item.list_id as string | null,
          place_id: item.place_id as string | null,
          photo_id: item.photo_id as string | null,
          saved_list_id: item.saved_list_id as string | null,
          created_at: item.created_at as string,
          profile: item.profile as ActivityItem["profile"],
          list: item.list as ActivityItem["list"],
          place: item.place as ActivityItem["place"],
          photo: item.photo as ActivityItem["photo"],
          reactions,
          user_reaction,
          comment_count: commentCount,
        };
      });

      const totalCount = count || 0;
      const hasMore = from + activities.length < totalCount;

      if (page === 0) {
        set({
          activities,
          currentPage: 0,
          hasMore,
          totalCount,
          loading: false,
        });
      } else {
        set((state) => ({
          activities: [...state.activities, ...activities],
          currentPage: page,
          hasMore,
          totalCount,
          loading: false,
        }));
      }
    } catch (error) {
      const message = "Failed to fetch activity feed";
      console.error(message, error);
      set({ error: message, loading: false });
    }
  },

  loadMore: async () => {
    const { hasMore, loading, currentPage, fetchFeed } = get();

    if (!hasMore || loading) return;

    await fetchFeed(currentPage + 1);
  },

  refreshFeed: async () => {
    set({ activities: [], currentPage: 0, hasMore: true });
    await get().fetchFeed(0);
  },

  clearActivities: () => {
    set({
      activities: [],
      currentPage: 0,
      hasMore: true,
      totalCount: 0,
      error: null,
    });
  },

  clearError: () => set({ error: null }),

  updateActivityReaction: (activityId, action, newReaction, previousReaction) => {
    set((state) => ({
      activities: state.activities.map((a) => {
        if (a.id !== activityId) return a;

        // Clone reactions array
        let reactions = [...a.reactions];
        const reactionTypes = useReactionStore.getState().reactionTypes;

        // Decrement old reaction count
        if (previousReaction) {
          reactions = reactions
            .map((r) =>
              r.reaction_type === previousReaction ? { ...r, count: r.count - 1 } : r
            )
            .filter((r) => r.count > 0);
        }

        // Increment new reaction count
        if (newReaction) {
          const existing = reactions.find((r) => r.reaction_type === newReaction);
          if (existing) {
            reactions = reactions.map((r) =>
              r.reaction_type === newReaction ? { ...r, count: r.count + 1 } : r
            );
          } else {
            const type = reactionTypes.find((rt) => rt.key === newReaction);
            reactions.push({
              reaction_type: newReaction,
              emoji: type?.emoji || "",
              count: 1,
            });
            // Re-sort by sort_order
            reactions.sort((a, b) => {
              const aOrder = reactionTypes.find((rt) => rt.key === a.reaction_type)?.sort_order || 999;
              const bOrder = reactionTypes.find((rt) => rt.key === b.reaction_type)?.sort_order || 999;
              return aOrder - bOrder;
            });
          }
        }

        return { ...a, reactions, user_reaction: newReaction };
      }),
    }));
  },
}));

export function groupPhotoActivities(activities: ActivityItem[]): GroupedActivityItem[] {
  const result: GroupedActivityItem[] = [];

  for (const activity of activities) {
    if (activity.activity_type !== "photo_uploaded" || !activity.place_id) {
      result.push(activity);
      continue;
    }

    const prev = result[result.length - 1];
    if (
      prev &&
      prev.activity_type === "photo_uploaded" &&
      prev.user_id === activity.user_id &&
      prev.place_id === activity.place_id &&
      prev.list_id === activity.list_id
    ) {
      // Merge into existing group
      if (!prev.grouped_photos) {
        prev.grouped_photos = prev.photo
          ? [{ id: prev.photo.id, file_url: prev.photo.file_url }]
          : [];
        prev.photo_count = 1;
      }
      if (activity.photo) {
        prev.grouped_photos.push({ id: activity.photo.id, file_url: activity.photo.file_url });
      }
      prev.photo_count = prev.grouped_photos.length;
    } else {
      // Start a new (potentially grouped) entry
      const grouped: GroupedActivityItem = { ...activity };
      if (activity.photo) {
        grouped.grouped_photos = [{ id: activity.photo.id, file_url: activity.photo.file_url }];
        grouped.photo_count = 1;
      }
      result.push(grouped);
    }
  }

  return result;
}
